import React, { forwardRef } from 'react';

import { ChevronDown, Eye } from 'lucide-react';

import Button from './Button';
import Typography from './Typography';

import type { LucideIcon } from 'lucide-react';

export interface SelectInputProps {
	label?: string;
	value: string;
	placeholder?: string;
	options: Array<{
		value: string;
		label: string;
		isDefault?: boolean;
		isCustom?: boolean;
	}>;
	onChange: (value: string) => void;
	onViewRates?: () => void;
	viewRatesLabel?: string;
	viewRatesIcon?: LucideIcon;
	disabled?: boolean;
	error?: string;
	helperText?: string;
	fullWidth?: boolean;
	className?: string;
	selectClassName?: string;
	viewRatesButtonClassName?: string;
	viewRatesTextClassName?: string;
	containerClassName?: string;
}

const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(
	(
		{
			label,
			value,
			placeholder = 'Select an option...',
			options,
			onChange,
			onViewRates,
			viewRatesLabel = 'View Rates',
			viewRatesIcon: ViewRatesIcon = Eye,
			disabled = false,
			error,
			helperText,
			fullWidth = true,
			className = '',
			selectClassName = '',
			viewRatesButtonClassName = '',
			viewRatesTextClassName = '',
			containerClassName = '',
		},
		ref,
	) => {
		const selectedOption = options.find((option) => option.value === value);

		const getDisplayText = () => {
			if (!selectedOption) return placeholder;

			let displayText = selectedOption.label;
			if (selectedOption.isDefault) {
				displayText += ' (Default)';
			}
			if (selectedOption.isCustom) {
				displayText += ' - ';
			}

			return displayText;
		};

		const getCustomPart = () => {
			if (!selectedOption || !selectedOption.isCustom) return null;
			return 'Custom*';
		};

		return (
			<div
				className={[containerClassName, fullWidth ? 'w-full' : '']
					.filter(Boolean)
					.join(' ')}
			>
				{label && (
					<Typography
						variant="caption"
						className="text-zinc-800 text-xs font-medium mb-1"
					>
						{label}
					</Typography>
				)}

				<div className="flex items-center gap-2.5">
					<div className="relative flex-1">
						<select
							ref={ref}
							value={value}
							onChange={(e) => onChange(e.target.value)}
							disabled={disabled}
							className={`
							w-full h-9 px-3.5 pr-10 bg-neutral-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-300
							text-sm font-normal text-transparent caret-transparent appearance-none
							focus:outline-none focus:ring-1 focus:ring-offset-0
							disabled:opacity-50 disabled:cursor-not-allowed
							${error ? 'outline-error-300 focus:ring-error-400' : ''}
							${className}
							${selectClassName}
						`
								.trim()
								.replace(/\s+/g, ' ')}
						>
							{/*
							 * The <select> itself is text-transparent so the styled
							 * overlay below renders the selected value. Options must set
							 * their own color/background, otherwise they inherit
							 * color: transparent and become invisible in the native
							 * popup on Chromium/Edge (Windows) — invisible on hover in
							 * particular. See SelectInput overlay pattern above.
							 */}
							<option value="" disabled className="bg-white text-zinc-400">
								{placeholder}
							</option>
							{options.map((option) => (
								<option
									key={option.value}
									value={option.value}
									className="bg-white text-zinc-800"
								>
									{option.label} {option.isDefault ? '(Default)' : ''}
								</option>
							))}
						</select>

						{/* Custom Display Text Overlay */}
						<div className="absolute inset-0 pointer-events-none flex items-center px-3.5">
							<Typography
								variant="body2"
								className="text-zinc-800 text-sm font-normal flex items-center gap-1"
							>
								{getDisplayText()}
								{selectedOption?.isCustom && (
									<span className="font-bold">{getCustomPart()}</span>
								)}
							</Typography>
							<ChevronDown className="w-3 h-3 ml-auto text-zinc-800" />
						</div>
					</div>

					{onViewRates && (
						<Button
							variant="outline"
							size="sm"
							onClick={onViewRates}
							disabled={!value || disabled}
							icon={ViewRatesIcon}
							className={`h-9 px-3 bg-white rounded-md border border-neutral-200 text-indigo-700 hover:border-indigo-300 ${viewRatesButtonClassName}`
								.trim()
								.replace(/\s+/g, ' ')}
						>
							<Typography
								variant="caption"
								className={`text-indigo-700 text-xs font-medium ${viewRatesTextClassName}`
									.trim()
									.replace(/\s+/g, ' ')}
							>
								{viewRatesLabel}
							</Typography>
						</Button>
					)}
				</div>

				{error && (
					<Typography variant="caption" className="text-red-600 mt-1">
						{error}
					</Typography>
				)}

				{helperText && !error && (
					<Typography variant="caption" className="text-gray-500 mt-1">
						{helperText}
					</Typography>
				)}
			</div>
		);
	},
);

SelectInput.displayName = 'SelectInput';

export default SelectInput;
